import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';

import { COLOR_SCALE, RADIUS_SCALE } from '@/components/design-system/tokens';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import {
  buildSupabaseImageUrl,
  buildSupabaseSrcSet,
  isSupabasePublicImageUrl,
  extractOriginalUrlFromCDN,
  isCloudflareCDNUrl,
} from '@/utils/supabase/imageTransforms';
const useVisibilityDetection = (priority: boolean, ref: React.RefObject<Element>) => {
  const [isInView, setIsInView] = useState(() => priority);

  // Helper function to check if element is already in viewport
  const checkIfInView = (element: Element | null): boolean => {
    if (!element) return false;
    try {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      return (
        rect.top >= -50 &&
        rect.left >= -50 &&
        rect.bottom <= windowHeight + 50 &&
        rect.right <= windowWidth + 50
      );
    } catch {
      // If getBoundingClientRect fails, assume not in view
      return false;
    }
  };

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    let observer: IntersectionObserver | null = null;
    let rafId = null;

    // Use requestAnimationFrame to ensure DOM is ready
    rafId = requestAnimationFrame(() => {
      if (!ref.current) {
        setIsInView(false);
        return;
      }

      try {
        // Check if element is already in viewport (common after pagination scrolls to section)
        const alreadyInView = checkIfInView(ref.current);
        if (alreadyInView) {
          setIsInView(true);
          return;
        }

        // Set up IntersectionObserver for lazy loading
        observer = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                setIsInView(true);
                if (observer) {
                  observer.disconnect();
                  observer = null;
                }
              }
            }
          },
          {
            rootMargin: '50px',
            threshold: 0.01,
          }
        );

        observer.observe(ref.current);
      } catch (error) {
        // If there's an error, fall back to showing the image
        console.warn('Error setting up image visibility detection:', error);
        setIsInView(true);
      }
    });

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [priority, ref]);

  return isInView;
};

interface OptimizedImageProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'onLoad' | 'onClick'
> {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  priority?: boolean;
  sizes?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  onLoad?: () => void;
  onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
  style?: React.CSSProperties;
}

/**
 * OptimizedImage Component
 *
 * Provides optimized image loading with:
 * - WebP/AVIF format support with fallbacks
 * - Responsive images with srcset
 * - Lazy loading with intersection observer
 * - Proper width/height to prevent layout shift
 * - Blur placeholder for better UX
 */
const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  sizes = '100vw',
  objectFit = 'cover',
  onLoad,
  onClick,
  onError,
  style,
  role: propRole,
  tabIndex: propTabIndex,
  ...props
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const imgRef = useRef<HTMLDivElement>(null);

  // Use custom hook for visibility detection
  const isInView = useVisibilityDetection(priority, imgRef);

  // Memoize parsed numeric dimensions to avoid recalculating on every render
  const numericDimensions = useMemo(() => {
    const numericWidth =
      typeof width === 'number'
        ? width
        : typeof width === 'string'
          ? Number.parseFloat(width)
          : undefined;
    const numericHeight =
      typeof height === 'number'
        ? height
        : typeof height === 'string'
          ? Number.parseFloat(height)
          : undefined;
    return { numericWidth, numericHeight };
  }, [width, height]);

  // Memoize expensive computations
  const imageConfig = useMemo(() => {
    const isSupabaseImage = isSupabasePublicImageUrl(src);
    const { numericWidth, numericHeight } = numericDimensions;

    const fallbackWidth = (() => {
      if (Number.isFinite(numericWidth) && typeof numericWidth === 'number') {
        const rounded = Math.round(numericWidth);
        return Math.min(Math.max(rounded * 2, rounded), 1920);
      }
      return 1080;
    })();

    const widths = [320, 480, 640, 750, 828, 1080, 1200, 1920];

    const webp = isSupabaseImage
      ? buildSupabaseSrcSet(src, widths, {
          format: 'webp',
          quality: 90,
          ...(numericHeight && { height: Math.round(numericHeight) }),
        })
      : null;

    const fallback = isSupabaseImage
      ? buildSupabaseImageUrl(src, {
          width: fallbackWidth,
          ...(numericHeight && { height: Math.round(numericHeight) }),
          quality: 75,
        })
      : src;

    return { webp, fallback, isSupabaseImage };
  }, [src, numericDimensions]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const img = event.currentTarget;

      // If we haven't already applied a fallback, try to use a default fallback
      if (!hasError && !img.dataset.fallbackApplied) {
        img.dataset.fallbackApplied = 'true';

        // First, try to extract original URL from CDN if it's a Cloudflare CDN URL
        // This handles the case where onerror=redirect parameter didn't work
        if (isCloudflareCDNUrl(img.src)) {
          const originalUrl = extractOriginalUrlFromCDN(img.src);
          if (originalUrl) {
            // Cloudflare CDN transformation failed (9422), falling back to original image
            setCurrentSrc(originalUrl);
            setHasError(false);
            setIsLoaded(false);
            return;
          }
        }

        // Try using the original src if it's different from fallback
        if (src && src !== imageConfig.fallback && !imageConfig.isSupabaseImage) {
          setCurrentSrc(src);
          setHasError(false);
          setIsLoaded(false);
          return;
        }
      }

      // If error persists, mark as error and show the image anyway
      // This prevents the image from staying invisible - set isLoaded to true
      // so the opacity becomes 100% and the image is visible (even if broken)
      setHasError(true);
      setIsLoaded(true);

      // Call onError prop if provided
      if (onError) {
        onError(event);
      }
    },
    [hasError, src, imageConfig.fallback, imageConfig.isSupabaseImage, onError]
  );

  // Update currentSrc when src prop changes
  useEffect(() => {
    setCurrentSrc(null);
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Memoize onKeyDown callback to prevent recreation on every render
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLImageElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        // Create a synthetic mouse event for the click handler
        const syntheticEvent = {
          ...e,
          clientX: 0,
          clientY: 0,
          pageX: 0,
          pageY: 0,
          screenX: 0,
          screenY: 0,
          button: 0,
          buttons: 0,
        } as unknown as React.MouseEvent<HTMLImageElement>;
        onClick?.(syntheticEvent);
      }
    },
    [onClick]
  );

  // Memoize layout calculations to avoid recalculating on every render
  const layoutConfig = useMemo(
    () => ({
      isCenteringMode: className.includes('max-w-full') || className.includes('max-h-full'),
      alignBottom: className.includes('items-end') || style?.alignItems === 'flex-end',
    }),
    [className, style?.alignItems]
  );

  // Don't render image until in view (unless priority)
  // If priority is true, always render (bypass lazy loading)
  const shouldRender = priority || isInView;

  if (!shouldRender) {
    return (
      <div
        ref={imgRef}
        className={`${COLOR_SCALE['skeleton-bg']} ${RADIUS_SCALE.md} shimmer-effect ${className}`}
        style={{ width, height }}
        aria-label={alt}
      />
    );
  }

  // Early return for no src - render placeholder
  if (!src) {
    return (
      <div
        className={`${COLOR_SCALE['skeleton-bg']} ${RADIUS_SCALE.md} shimmer-effect ${className}`}
        style={{ width, height }}
        aria-label={alt}
      />
    );
  }

  return (
    <div
      ref={imgRef}
      className={`relative w-full h-full ${
        layoutConfig.isCenteringMode
          ? `flex ${layoutConfig.alignBottom ? 'items-end' : 'items-center'} justify-center`
          : 'overflow-hidden'
      }`}
      style={layoutConfig.isCenteringMode ? {} : {}}
    >
      {/* Blur placeholder */}
      {!isLoaded && (
        <div
          className={`absolute inset-0 ${COLOR_SCALE['skeleton-bg']} ${RADIUS_SCALE.md} shimmer-effect`}
        />
      )}

      <picture className={layoutConfig.isCenteringMode ? 'block' : 'block w-full h-full'}>
        {!currentSrc && imageConfig.webp && (
          <source type="image/webp" srcSet={imageConfig.webp} sizes={sizes} />
        )}
        <img
          src={currentSrc ?? imageConfig.fallback}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
          role={onClick ? 'button' : propRole}
          tabIndex={onClick ? 0 : propTabIndex}
          onKeyDown={onClick ? handleKeyDown : undefined}
          aria-label={onClick ? alt : undefined}
          className={`${className} transition-opacity duration-300 block ${
            isLoaded || hasError ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ objectFit, display: 'block', ...style }}
          {...props}
        />
      </picture>
    </div>
  );
};

// Memoized OptimizedImage with optimized comparison function
const MemoizedOptimizedImage = memo(OptimizedImage, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Shallow compare style object properties
  const styleEqual = (() => {
    if (!prevProps.style && !nextProps.style) return true;
    if (!prevProps.style || !nextProps.style) return false;

    const prevKeys = Object.keys(prevProps.style);
    const nextKeys = Object.keys(nextProps.style);

    if (prevKeys.length !== nextKeys.length) return false;

    for (const key of prevKeys) {
      const prevValue = prevProps.style?.[key as keyof React.CSSProperties];
      const nextValue = nextProps.style?.[key as keyof React.CSSProperties];
      if (prevValue !== nextValue) return false;
    }

    return true;
  })();

  // Compare className by content rather than reference to handle template literal recreation
  const classNameEqual =
    prevProps.className === nextProps.className ||
    (typeof prevProps.className === 'string' &&
      typeof nextProps.className === 'string' &&
      prevProps.className.trim() === nextProps.className.trim());

  return (
    prevProps.src === nextProps.src &&
    prevProps.alt === nextProps.alt &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    classNameEqual &&
    prevProps.priority === nextProps.priority &&
    prevProps.sizes === nextProps.sizes &&
    prevProps.objectFit === nextProps.objectFit &&
    prevProps.onLoad === nextProps.onLoad &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.onError === nextProps.onError &&
    prevProps.role === nextProps.role &&
    prevProps.tabIndex === nextProps.tabIndex &&
    styleEqual
  );
});

const OptimizedImageWithMonitor = withMemoryLeakMonitor(MemoizedOptimizedImage, {
  componentName: 'OptimizedImage',
  // Let global config decide monitoring (respects exclusion list)
  renderThreshold: 100, // Increased threshold for image components
  frequencyThreshold: 15, // Increased frequency threshold for image components
});

export default OptimizedImageWithMonitor;
